import React, { useEffect, useState } from "react";
import { useHistoryBack } from "../lib/useHistoryBack";
import { X, Loader2, Package, ArrowLeft, Search, ChevronDown } from "lucide-react";
import api, { formatMoney, formatMoney2, formatNumber, ABCD_ORDER, ABCD_COLORS, ABCD_LABELS } from "../lib/api";
import { ExportButtons } from "../lib/exports";
import { ServerProductsExport, col } from "../lib/exportCols";
import { ventasLabel } from "../lib/periodo";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { useSortable, SortHeader } from "../lib/sort";
import SustitutivosFamilia from "./SustitutivosFamilia";

function AbcdPill({ code }) {
  return <span className={`abcd-pill abcd-${code}`}>{code}</span>;
}

/**
 * DrillDown slide-over panel.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - studyId: string
 *  - title: string (header title)
 *  - subtitle?: string
 *  - filters: { familia?, laboratorio?, abcd? } (sent to /products endpoint)
 *  - abcdBreakdown?: { code, count, value, label }[]
 *      Optional pre-computed ABCD distribution to render as mini chart at top.
 *  - familyBreakdown?: { familia, sales, stock }[]
 *      Optional: when provided AND no family filter is set, render an intermediate
 *      "by family" view first. Clicking a family adds the familia filter and shows products.
 *  - chartTitle?: string
 */
export default function DrillDown({ open, onClose, studyId, title, subtitle, filters, abcdBreakdown, familyBreakdown, chartTitle, periodo }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [view, setView] = useState("productos");
  const [q, setQ] = useState("");
  const [verAbcd, setVerAbcd] = useState(() => localStorage.getItem("dd-ver-abcd") === "1");
  const toggleAbcd = () => setVerAbcd((v) => { localStorage.setItem("dd-ver-abcd", v ? "0" : "1"); return !v; });
  const [qd, setQd] = useState("");
  useEffect(() => { const t = setTimeout(() => setQd(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setQ(""); setQd(""); }, [open, JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps
  const nav = useHistoryBack(open, (d) => (d === 0 ? onClose() : setSelectedFamily(null)));

  // Reset selected family when drilldown opens with a new context
  useEffect(() => {
    if (open) setSelectedFamily(null);
  }, [open, JSON.stringify(filters)]);

  useEffect(() => { setView("productos"); }, [open, selectedFamily, filters?.familia]);

  // Effective filters: original filters + family selection (when user clicks a family row)
  const effectiveFilters = React.useMemo(
    () => (selectedFamily ? { ...filters, familia: selectedFamily } : filters),
    [filters, selectedFamily]
  );

  // Decide if we should show the family grouping screen first.
  const showFamilyView = !!familyBreakdown && !filters?.familia && !selectedFamily && !qd;
  const canSust = !showFamilyView && !!effectiveFilters?.familia;
  const showSust = canSust && view === "sustitutivos";

  useEffect(() => {
    if (!open || !studyId || showFamilyView) return;
    (async () => {
      setLoading(true);
      try {
        const params = { limit: 200, sort_by: "tot_venta", sort_dir: "desc", ...effectiveFilters, ...(qd ? { search: qd } : {}) };
        const { data: d } = await api.get(`/studies/${studyId}/products`, { params });
        setData(d);
      } finally { setLoading(false); }
    })();
  }, [open, studyId, JSON.stringify(effectiveFilters), showFamilyView, qd]);

  // Aggregates for the family view (use the pre-computed pivot) — must run before any early return.
  const familyAgg = React.useMemo(() => {
    if (!familyBreakdown) return { totSales: 0, totStock: 0, items: [] };
    const items = familyBreakdown.filter((f) => (f.sales || 0) > 0 || (f.stock || 0) > 0);
    const totSales = items.reduce((a, b) => a + (b.sales || 0), 0);
    const totStock = items.reduce((a, b) => a + (b.stock || 0), 0);
    return { totSales, totStock, items };
  }, [familyBreakdown]);

  // Sort hooks (must run before the early return). They work on the loaded
  // data; when the data is empty they simply return an empty array.
  const familySort = useSortable(familyAgg.items, "sales", "desc");
  const productsSort = useSortable(data.items, null, "desc");

  if (!open) return null;

  // Summary metrics from loaded items (only relevant when products are loaded)
  const totalStock = data.sums?.valor_pvp || 0;
  const totalSales = data.sums?.tot_venta || 0;
  const totalUnits = data.sums?.uds_vendidas || 0;

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[55] fade-in"
        onClick={nav.close}
        data-testid="drilldown-overlay"
      />
      <div
        className="fixed top-0 right-0 h-screen w-full bg-white shadow-2xl z-[60] flex flex-col fade-in"
        data-testid="drilldown-panel"
        style={{ animation: "slideIn 220ms ease-out" }}
      >
        <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
        {/* Header */}
        <div className="px-6 py-2.5 border-b border-slate-200 flex flex-wrap sm:flex-nowrap items-center justify-between gap-4">
          <div className="min-w-0 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <button onClick={nav.back} title={selectedFamily ? "Volver a las familias" : "Volver a la página anterior"}
                    className="shrink-0 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-3 pr-4 py-1.5 text-sm font-semibold shadow hover:bg-slate-700 transition-colors"
                    data-testid={selectedFamily ? "drilldown-back-to-families" : "drilldown-back"}>
              <ArrowLeft size={18} /> Volver
            </button>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Desglose{selectedFamily && <> · <button onClick={nav.back} className="hover:underline text-blue-700" data-testid="drilldown-breadcrumb-familias">Familias</button> › {selectedFamily}</>}
            </div>
            <h2 className="font-display text-lg font-extrabold tracking-tight leading-tight truncate" title={title}>{title}</h2>
            {subtitle && <p className="text-xs text-slate-600 truncate">{subtitle}</p>}
          </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {showFamilyView ? (
              <ExportButtons filename={`${title || "familias"}-familias`} title={title} subtitle={subtitle} sheetName="Familias" testIdPrefix="drilldown-familias"
                             rows={familySort.sortedRows.map((f) => ({ ...f, pct: familyAgg.totSales > 0 ? f.sales / familyAgg.totSales : 0, vs: f.stock > 0 ? f.sales / f.stock : null }))}
                             columns={[col.text("Familia", "familia"), col.eur("Valor stock", "stock"), col.eur("Ventas", "sales"), col.pct("% Ventas", "pct"), col.num("V/S", "vs")]} />
            ) : !showSust && (
              <ServerProductsExport studyId={studyId} filters={effectiveFilters} subtitle={subtitle}
                                    title={selectedFamily ? `${title} · ${selectedFamily}` : title}
                                    filename={selectedFamily ? `${title}-${selectedFamily}` : (title || "productos")} testIdPrefix="drilldown-export" />
            )}
            <button onClick={nav.close} className="p-2 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900" data-testid="drilldown-close">
              <X size={18} />
            </button>
          </div>
        </div>

        {canSust && (
          <div className="px-6 pt-1 flex gap-2 border-b border-slate-200" data-testid="drilldown-view-tabs">
            {[["productos", "Productos"], ["sustitutivos", "Sustitutivos parafarmacia"]].map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`drilldown-tab-${k}`}
                className={"px-3 py-1.5 text-sm font-semibold border-b-2 -mb-px transition-colors " + (view === k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800")}>
                {l}
              </button>
            ))}
          </div>
        )}

        {/* Stats — family view shows pivot aggregates, products view shows loaded items */}
        <div className={(showSust ? "hidden " : "") + "px-6 py-2 grid grid-cols-2 md:grid-cols-5 gap-x-3 gap-y-1 border-b border-slate-200 bg-slate-50"}>
          {showFamilyView ? (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Familias</div>
                <div className="font-display text-base font-extrabold leading-tight">{formatNumber(familyAgg.items.length)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Valor stock</div>
                <div className="font-display text-base font-extrabold leading-tight">{formatMoney(familyAgg.totStock)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold" data-testid="drilldown-ventas-label">{ventasLabel(periodo)}</div>
                <div className="font-display text-base font-extrabold leading-tight">{formatMoney(familyAgg.totSales)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">% Rotación (V/S)</div>
                <div className="font-display text-base font-extrabold leading-tight">
                  {familyAgg.totStock > 0 ? `${formatNumber(Math.round((100 * familyAgg.totSales) / familyAgg.totStock))} % · ${(familyAgg.totSales / familyAgg.totStock).toFixed(2).replace(".", ",")}×` : "—"}
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Productos</div>
                <div className="font-display text-base font-extrabold leading-tight">{formatNumber(data.total)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Valor stock</div>
                <div className="font-display text-base font-extrabold leading-tight">{formatMoney(totalStock)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold" data-testid="drilldown-ventas-label">{ventasLabel(periodo)}</div>
                <div className="font-display text-base font-extrabold leading-tight">{formatMoney(totalSales)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Uds. vendidas</div>
                <div className="font-display text-base font-extrabold leading-tight">{formatNumber(totalUnits)}</div>
              </div>
              <div data-testid="drilldown-rotacion" title="Ventas del periodo divididas entre el valor del stock (PVP)">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">% Rotación{effectiveFilters?.laboratorio ? " del laboratorio" : ""}</div>
                {totalStock > 0 ? (
                  <div className="mt-0.5 flex items-baseline gap-2">
                    <span className={"font-display text-base font-extrabold " + (totalSales >= totalStock ? "text-emerald-600" : "text-red-600")}>
                      {formatNumber(Math.round((100 * totalSales) / totalStock))} %
                    </span>
                    <span className="text-xs text-slate-500">{(totalSales / totalStock).toFixed(2).replace(".", ",")}× ventas / stock</span>
                  </div>
                ) : <div className="font-display text-base font-extrabold leading-tight">—</div>}
              </div>
            </>
          )}
        </div>

        {studyId && !showSust && (
          <div className="px-6 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 flex-wrap" data-testid="drilldown-search-bar">
            <div className="relative flex-1 min-w-[240px] max-w-xl">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto por nombre o CN…"
                     className="input w-full" style={{ paddingLeft: 34, paddingRight: 32 }} data-testid="drilldown-search" />
              {q && (
                <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700" title="Borrar búsqueda" data-testid="drilldown-search-clear">
                  <X size={14} />
                </button>
              )}
            </div>
            {qd && !loading && (
              <span className="text-xs text-slate-600" data-testid="drilldown-search-count">
                {formatNumber(data.total)} producto{data.total === 1 ? "" : "s"} con «{qd}»{familyBreakdown && !effectiveFilters?.familia ? " en todas las familias" : ""}
              </span>
            )}
          </div>
        )}
        {!showFamilyView && !showSust && abcdBreakdown && abcdBreakdown.length > 0 && (
          <div className={"px-6 border-b border-slate-200 " + (verAbcd ? "py-3" : "py-1.5")}>
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={toggleAbcd} className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-900" data-testid="drilldown-toggle-abcd">
                <ChevronDown size={14} className={"transition-transform " + (verAbcd ? "rotate-180" : "")} /> {chartTitle || "Distribución ABCD"} · {verAbcd ? "ocultar" : "ver gráfico"}
              </button>

            {/* Mini Efficiency comparison: %ventas vs %stock within this subset */}
            {(() => {
              const totalSales = abcdBreakdown.reduce((a, b) => a + (b.value || 0), 0);
              const stockValues = abcdBreakdown.filter((b) => "stock" in b);
              const hasStock = stockValues.length > 0;
              const totalStock = hasStock ? abcdBreakdown.reduce((a, b) => a + (b.stock || 0), 0) : 0;
              if (!hasStock) return null;
              const sumKey = (codes, field) => codes.reduce((a, c) => {
                const r = abcdBreakdown.find((x) => x.code === c);
                return a + (r?.[field] || 0);
              }, 0);
              const altaV = totalSales > 0 ? sumKey(["AA","A","BB","B"], "value") / totalSales : 0;
              const altaS = totalStock > 0 ? sumKey(["AA","A","BB","B"], "stock") / totalStock : 0;
              const ok = altaV >= altaS;
              if (!verAbcd) return (
                <span className="text-xs text-slate-600 flex items-center gap-2 flex-wrap" data-testid="drill-efficiency">
                  <span className={"font-bold px-2 py-0.5 rounded " + (ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")}>{ok ? "✓ Equilibrio" : "⚠ Exceso de stock"}</span>
                  Ventas alta rot. <b className="text-emerald-700">{(altaV * 100).toFixed(1).replace(".", ",")} %</b> · Stock alta rot. <b>{(altaS * 100).toFixed(1).replace(".", ",")} %</b> ·
                  <b className={ok ? "text-emerald-700" : "text-red-600"}>{altaV >= altaS ? "+" : ""}{((altaV - altaS) * 100).toFixed(1).replace(".", ",")} pp</b>
                </span>
              );
              return (
                <div className="w-full mt-2 mb-3 p-3 rounded bg-slate-50 border border-slate-200" data-testid="drill-efficiency">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Eficiencia interna (alta rotación AA·A·BB·B)</div>
                    <span className={"text-[11px] font-bold px-2 py-0.5 rounded " + (ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")}>
                      {ok ? "✓ Equilibrio" : "⚠ Exceso de stock"}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-2 text-sm">
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest">% Ventas alta rot.</div>
                      <div className="font-display text-xl font-extrabold text-emerald-600">{(altaV*100).toFixed(1).replace(".", ",")} %</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest">% Stock alta rot.</div>
                      <div className="font-display text-xl font-extrabold text-slate-900">{(altaS*100).toFixed(1).replace(".", ",")} %</div>
                    </div>
                    <div title="Diferencia en puntos porcentuales entre el % de ventas y el % de stock que son de alta rotación. Positivo: el stock de alta rotación rinde más de lo que ocupa.">
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest">Diferencia (puntos)</div>
                      <div className={"font-display text-xl font-extrabold " + (ok ? "text-emerald-600" : "text-red-600")}>
                        {altaV >= altaS ? "+" : ""}{((altaV - altaS) * 100).toFixed(1).replace(".", ",")} pp
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">% ventas − % stock de alta rotación</div>
                    </div>
                  </div>
                </div>
              );
            })()}
            </div>

            {verAbcd && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={abcdBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="code" tick={{ fontSize: 11, fontWeight: 600 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
                  <Tooltip
                    contentStyle={{ borderRadius: 6, fontSize: 12 }}
                    formatter={(v, k) => k === "value" ? formatMoney(v) : formatNumber(v)}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {abcdBreakdown.map((d) => <Cell key={d.code} fill={ABCD_COLORS[d.code]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="text-xs">
                <table className="dense" style={{ fontSize: 11 }}>
                  <thead><tr><th>Clase</th><th>Periodo</th>{abcdBreakdown.some((d) => d.count != null) && <th className="num">Productos</th>}<th className="num">Valor</th></tr></thead>
                  <tbody>
                    {abcdBreakdown.map((d) => (
                      <tr key={d.code}>
                        <td><AbcdPill code={d.code} /></td>
                        <td className="font-mono text-[10px]">{ABCD_LABELS[d.code]}</td>
                        {abcdBreakdown.some((x) => x.count != null) && <td className="num">{formatNumber(d.count || 0)}</td>}
                        <td className="num">{formatMoney(d.value || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>}
          </div>
        )}

        {/* Body: family list OR products list */}
        <div className="flex-1 overflow-auto">
          {showSust ? (
            <SustitutivosFamilia studyId={studyId} familia={effectiveFilters.familia} />
          ) : showFamilyView ? (
            familyAgg.items.length === 0 ? (
              <div className="p-12 text-center">
                <Package size={32} className="mx-auto text-slate-400" />
                <p className="text-slate-500 mt-2">Sin familias con datos en este desglose.</p>
              </div>
            ) : (
              <div>
                <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 text-xs text-blue-900">
                  <b>Paso 1/2:</b> Selecciona una familia para ver sus productos detallados.
                </div>
                <table className="dense" data-testid="drilldown-families-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th><SortHeader label="Familia" k="familia" sort={familySort} defaultDir="asc" /></th>
                      <th className="num"><SortHeader label="Valor stock" k="stock" sort={familySort} num /></th>
                      <th className="num"><SortHeader label="Ventas" k="sales" sort={familySort} num /></th>
                      <th className="num">% Ventas</th>
                      <th className="num">V/S</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {familySort.sortedRows.map((f, i) => {
                      const pct = familyAgg.totSales > 0 ? (f.sales / familyAgg.totSales) : 0;
                      const vs = (f.stock || 0) > 0 ? (f.sales / f.stock) : 0;
                      return (
                        <tr
                          key={f.familia}
                          className="cursor-pointer hover:bg-blue-50"
                          onClick={() => { setSelectedFamily(f.familia); nav.push(); }}
                          data-testid={`drill-family-row-${f.familia}`}
                        >
                          <td className="font-mono text-slate-400">{i + 1}</td>
                          <td className="font-semibold">{f.familia}</td>
                          <td className="num">{formatMoney(f.stock || 0)}</td>
                          <td className="num">{formatMoney(f.sales || 0)}</td>
                          <td className="num text-slate-600">{(pct * 100).toFixed(1)}%</td>
                          <td className={"num font-bold " + (vs >= 1 ? "text-emerald-700" : "text-red-600")}>
                            {vs > 0 ? `${vs.toFixed(2)}×` : "—"}
                          </td>
                          <td className="text-blue-600 text-xs font-semibold">Ver productos →</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : loading ? (
            <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Cargando productos…</div>
          ) : data.items.length === 0 ? (
            <div className="p-12 text-center">
              <Package size={32} className="mx-auto text-slate-400" />
              <p className="text-slate-500 mt-2">{qd ? `Ningún producto coincide con «${qd}».` : "Sin productos en este desglose."}</p>
            </div>
          ) : (
            <table className={"dense " + (effectiveFilters?.laboratorio ? "" : "compact")} data-testid="drilldown-table">
              <thead>
                <tr>
                  <th><SortHeader label="CN" k="cn" sort={productsSort} defaultDir="asc" /></th>
                  <th><SortHeader label="Descripción" k="descripcion" sort={productsSort} defaultDir="asc" /></th>
                  {!effectiveFilters.familia && <th><SortHeader label="Familia" k="familia" sort={productsSort} defaultDir="asc" /></th>}
                  {!effectiveFilters.laboratorio && <th><SortHeader label="Laboratorio" k="laboratorio" sort={productsSort} defaultDir="asc" /></th>}
                  <th className="num"><SortHeader label="Stock" k="stock" sort={productsSort} num /></th>
                  <th className="num"><SortHeader label="PVP" k="pvp" sort={productsSort} num /></th>
                  <th className="num"><SortHeader label="Uds. Vendidas" k="uds_vendidas" sort={productsSort} num /></th>
                  <th className="num"><SortHeader label="Valor PVP" k="valor_pvp" sort={productsSort} num /></th>
                  <th className="num"><SortHeader label="Ventas" k="tot_venta" sort={productsSort} num /></th>
                  <th><SortHeader label="Última compra" k="u_entrada" sort={productsSort} defaultDir="desc" /></th>
                  <th><SortHeader label="Última venta" k="u_salida" sort={productsSort} defaultDir="desc" /></th>
                  {!effectiveFilters.abcd && <th><SortHeader label="ABCD" k="abcd" sort={productsSort} defaultDir="asc" /></th>}
                </tr>
              </thead>
              <tbody>
                {productsSort.sortedRows.map((p, i) => (
                  <tr key={`d-${i}-${p.cn}`}>
                    <td className="font-mono whitespace-nowrap">{p.cn}</td>
                    <td className="desc">{p.descripcion}</td>
                    {!effectiveFilters.familia && <td className="text-[10px]">{p.familia}</td>}
                    {!effectiveFilters.laboratorio && <td className="text-[10px]">{p.laboratorio}</td>}
                    <td className="num">{formatNumber(p.stock)}</td>
                    <td className="num">{formatMoney2(p.pvp)}</td>
                    <td className="num">{formatNumber(p.uds_vendidas)}</td>
                    <td className="num">{formatMoney2(p.valor_pvp)}</td>
                    <td className="num">{formatMoney2(p.tot_venta)}</td>
                    <td className="text-xs font-mono whitespace-nowrap">{p.u_entrada ? new Date(p.u_entrada).toLocaleDateString("es-ES") : "—"}</td>
                    <td className="text-xs font-mono whitespace-nowrap">{p.u_salida ? new Date(p.u_salida).toLocaleDateString("es-ES") : "—"}</td>
                    {!effectiveFilters.abcd && <td><AbcdPill code={p.abcd} /></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!showFamilyView && !showSust && data.total > data.items.length && (
            <div className="px-6 py-3 text-xs text-slate-500 bg-slate-50 border-t border-slate-200">
              Mostrando los primeros {data.items.length} de {formatNumber(data.total)} productos (ordenados por ventas).
            </div>
          )}
        </div>
      </div>
    </>
  );
}
